import { useCallback, useReducer, useRef, type Dispatch } from 'react';
import { boundPosition } from './graphLayout';
import type { GraphPosition, GraphPositions, LayoutState } from './types';

type Action =
	| { type: 'start'; positions: GraphPositions }
	| { type: 'move'; id: string; position: GraphPosition }
	| { type: 'save' }
	| { type: 'finish' }
	| { type: 'fail'; message: string };

export function layoutTransition(
	state: LayoutState,
	action: Action,
): LayoutState {
	switch (action.type) {
		case 'start':
			if (state.status !== 'view') {
				return state;
			}
			return { status: 'editing', positions: action.positions };
		case 'move':
			if (state.status !== 'editing' && state.status !== 'error') {
				return state;
			}
			return {
				status: 'editing',
				positions: {
					...state.positions,
					[action.id]: boundPosition(action.position),
				},
			};
		case 'save':
			if (state.status !== 'editing' && state.status !== 'error') {
				return state;
			}
			return { status: 'saving', positions: state.positions };
		case 'finish':
			return { status: 'view' };
		case 'fail':
			if (state.status !== 'saving') {
				return state;
			}
			return {
				status: 'error',
				positions: state.positions,
				message: action.message,
			};
	}
}

export function useGraphLayout(
	onSave?: (positions: GraphPositions) => Promise<void>,
): {
	state: LayoutState;
	dispatch: Dispatch<Action>;
	save: () => Promise<void>;
} {
	const [state, dispatch] = useReducer(layoutTransition, { status: 'view' });
	const pending = useRef(false);
	const save = useCallback(async (): Promise<void> => {
		if (
			pending.current ||
			!onSave ||
			(state.status !== 'editing' && state.status !== 'error')
		) {
			return;
		}
		pending.current = true;
		dispatch({ type: 'save' });
		try {
			await onSave(state.positions);
			dispatch({ type: 'finish' });
		} catch {
			dispatch({
				type: 'fail',
				message:
					'Layout could not be saved. Retry or cancel to restore the saved layout.',
			});
		} finally {
			pending.current = false;
		}
	}, [onSave, state]);
	return { state, dispatch, save };
}
